import React, { useId, useState } from 'react';

export const tokens = {
  border: '#dee2e6',
  surface: '#ffffff',
  surfaceMuted: '#f8f9fa',
  primary: '#007bff',
  primarySoft: '#e7f1ff',
  text: '#333333',
  textMuted: '#666666',
  textFaint: '#999999',
  danger: '#b3261e',
  radius: 6,
};

type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const BADGE_TONES: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: '#eceff1', fg: '#455a64' },
  info: { bg: '#e7f1ff', fg: '#0b5ed7' },
  success: { bg: '#e6f4ea', fg: '#1e7e34' },
  warning: { bg: '#fff4e5', fg: '#a15c00' },
  danger: { bg: '#fdecea', fg: tokens.danger },
};

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: React.ReactNode }) {
  const { bg, fg } = BADGE_TONES[tone];
  return (
    <span
      style={{
        background: bg,
        color: fg,
        borderRadius: 999,
        padding: '0.15rem 0.6rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

type ButtonVariant = 'primary' | 'default' | 'subtle';

export function Button({
  variant = 'default',
  style,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  const base: React.CSSProperties = {
    padding: '0.45rem 0.9rem',
    borderRadius: tokens.radius,
    fontSize: '0.9rem',
    cursor: rest.disabled ? 'not-allowed' : 'pointer',
    opacity: rest.disabled ? 0.55 : 1,
  };
  const variants: Record<ButtonVariant, React.CSSProperties> = {
    primary: { background: tokens.primary, color: '#fff', border: `1px solid ${tokens.primary}` },
    default: { background: tokens.surface, color: tokens.text, border: `1px solid ${tokens.border}` },
    subtle: { background: 'transparent', color: tokens.textMuted, border: '1px solid transparent' },
  };
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      className={['dv-btn', className].filter(Boolean).join(' ')}
      style={{ ...base, ...variants[variant], ...style }}
    />
  );
}

export function Card({
  title,
  actions,
  children,
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        marginBottom: '1.25rem',
        overflow: 'hidden',
      }}
    >
      {(title || actions) && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            padding: '0.75rem 1rem',
            borderBottom: `1px solid ${tokens.border}`,
            background: tokens.surfaceMuted,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
          {actions}
        </header>
      )}
      <div style={{ padding: '1rem' }}>{children}</div>
    </section>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ id: T; label: string }>;
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div
      role="tablist"
      style={{
        display: 'inline-flex',
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        overflow: 'hidden',
        flexWrap: 'wrap',
      }}
    >
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.id)}
            style={{
              padding: '0.45rem 0.9rem',
              fontSize: '0.9rem',
              fontWeight: selected ? 600 : 400,
              background: selected ? tokens.primarySoft : tokens.surface,
              color: selected ? tokens.primary : tokens.text,
              border: 'none',
              borderRight: `1px solid ${tokens.border}`,
              cursor: 'pointer',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'grid', gap: '0.3rem' }}>
      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>{label}</span>
      {children}
    </label>
  );
}

export const inputStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  border: `1px solid ${tokens.border}`,
  borderRadius: tokens.radius,
  fontSize: '0.9rem',
  color: tokens.text,
  background: tokens.surface,
  width: '100%',
  boxSizing: 'border-box',
};

export function BubbleInput({
  values,
  onChange,
  suggestions = [],
  placeholder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const listId = useId();

  const add = (incoming: string[]) => {
    const next = [...values];
    incoming.forEach((item) => {
      const trimmed = item.trim();
      if (trimmed && !next.includes(trimmed)) next.push(trimmed);
    });
    if (next.length !== values.length) onChange(next);
  };

  const takeDraft = (text: string) => {
    if (!text.includes(',')) {
      setDraft(text);
      return;
    }
    const parts = text.split(',');
    const remainder = parts.pop() ?? '';
    add(parts);
    setDraft(remainder.replace(/^\s+/, ''));
  };

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    add([trimmed]);
    setDraft('');
  };

  return (
    <div
      style={{
        ...inputStyle,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.35rem',
        alignItems: 'center',
        height: 'auto',
        minHeight: '2.15rem',
      }}
    >
      <input
        value={draft}
        placeholder={values.length === 0 ? placeholder : ''}
        list={suggestions.length ? listId : undefined}
        onChange={(event) => takeDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitDraft();
          }
        }}
        onBlur={commitDraft}
        style={{
          order: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: tokens.text,
          fontSize: '0.9rem',
          flex: '1 1 8rem',
          minWidth: '8rem',
          padding: 0,
        }}
      />
      {values.map((value) => (
        <span
          key={value}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.15rem',
            background: tokens.primarySoft,
            color: tokens.primary,
            borderRadius: 999,
            padding: '0.05rem 0.15rem 0.05rem 0.5rem',
            fontSize: '0.8rem',
            lineHeight: 1.4,
          }}
        >
          {value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange(values.filter((item) => item !== value))}
            style={{
              border: 'none',
              background: 'transparent',
              color: tokens.primary,
              cursor: 'pointer',
              fontSize: '0.95rem',
              lineHeight: 1,
              padding: '0 0.25rem',
            }}
          >
            ×
          </button>
        </span>
      ))}
      {suggestions.length > 0 && (
        <datalist id={listId}>
          {suggestions.map((option) => (
            <option key={option} value={option} />
          ))}
        </datalist>
      )}
    </div>
  );
}

export function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '1.5rem',
        textAlign: 'center',
        color: tokens.textFaint,
        fontSize: '0.9rem',
        background: tokens.surfaceMuted,
        borderRadius: tokens.radius,
      }}
    >
      {children}
    </div>
  );
}
